import {
  signInWithRedirect,
  signOut,
  fetchUserAttributes,
  getCurrentUser,
  fetchAuthSession
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useEffect, useState } from "react";
import { CognitoUserData } from "../../../interfaces/account.interface";

const getAuthenticatedUserWithAttributes = async (): Promise<CognitoUserData | null> => {
  try {
    const userAttributes = await fetchUserAttributes();
    const user = await getCurrentUser();

    if (!user) return null;

    return {
      username: user.username,
      attributes: userAttributes
    } as unknown as CognitoUserData;
  } catch {
    return null;
  }
};

const getAuthenticatedUser = async (): Promise<CognitoUserData | null> => {
  try {
    const user = await getCurrentUser();

    return {
      username: user.username
    };
  } catch {
    return null;
  }
};

const useAuth = (): {
  currentUser: CognitoUserData | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
} => {
  const [currentUser, setCurrentUser] = useState<CognitoUserData | null>(null);

  useEffect(() => {
    const updateUser = async () => {
      const user = await getAuthenticatedUser();

      if (!user) return setCurrentUser(null);

      if (currentUser?.username !== user.username) {
        setCurrentUser(user);
      }

      setCurrentUser(await getAuthenticatedUserWithAttributes());
    };

    const hubListener = Hub.listen("auth", updateUser);

    updateUser();

    return () => hubListener();
  }, []);

  const handleSignIn = () => signInWithRedirect();
  const handleSignOut = async () => {
    await signOut();
  };

  return { currentUser, signIn: handleSignIn, signOut: handleSignOut };
};

export default useAuth;

export { getAuthenticatedUserWithAttributes as getCurrentUser };

export const getAccessToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();

    return session.tokens?.idToken?.toString() || "";
  } catch {
    return "";
  }
};
